import { areWatchFeaturesLocked } from "@/domains/billing/lib/planAccess";
import { apiRequest } from "@/shared/lib/apiClient";
import { queryKeys } from "@/shared/lib/queryKeys";
import type { BriefHistoryItem, DailyBrief } from "@/shared/lib/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, ReactNode, useCallback, useContext } from "react";
import { useAppContext } from "./AppContext";

interface BriefContextType {
  brief: DailyBrief | null;
  briefHistory: BriefHistoryItem[];
  loading: boolean;
  refreshBrief: () => Promise<void>;
}

const BriefContext = createContext<BriefContextType | undefined>(undefined);

export const BriefProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { isAuthenticated, profile } = useAppContext();
  const enabled = isAuthenticated && !areWatchFeaturesLocked(profile);

  const todayQuery = useQuery({
    queryKey: queryKeys.briefToday(),
    queryFn: ({ signal }) => apiRequest<DailyBrief | null>("GET", "/api/brief/today", { signal }),
    enabled,
    staleTime: 60_000,
  });

  const historyQuery = useQuery({
    queryKey: queryKeys.briefHistory(),
    queryFn: async ({ signal }) => {
      const data = await apiRequest<{ briefs?: BriefHistoryItem[] } | null>("GET", "/api/brief/history", { signal });
      return data?.briefs ?? [];
    },
    enabled,
    staleTime: 60_000,
  });

  const refreshBrief = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.briefRoot() });
  }, [queryClient]);

  return (
    <BriefContext.Provider
      value={{
        brief: todayQuery.data ?? null,
        briefHistory: historyQuery.data ?? [],
        loading: todayQuery.isPending || todayQuery.isFetching || historyQuery.isPending || historyQuery.isFetching,
        refreshBrief,
      }}
    >
      {children}
    </BriefContext.Provider>
  );
};

export const useBriefContext = () => {
  const context = useContext(BriefContext);
  if (context === undefined) {
    throw new Error("useBriefContext must be used within a BriefProvider");
  }
  return context;
};
